import { MINOR_EXPONENT } from "./tariff";

/**
 * Typing a price. pay-ui spec §6.1, §11.1.
 *
 * The mirror image of `tariff.ts`: that file turns minor units into something an
 * operator reads, this one turns what an operator types back into minor units. It
 * is a deliberate mirror of `api/routes/tariffs.ts` as well — the server is the
 * gate and nothing here is trusted, but a mistyped maximum should be caught before
 * the round trip, and both halves cite the same reasons so a drift is visible.
 *
 * ## Why the arithmetic is string arithmetic
 *
 * `Number("2.67") * 100` is `266.99999999999994`. Every conversion here is a split
 * on the decimal point, a pad, and a concatenation, so a figure the operator typed
 * is the figure the server stores — exactly, with no cent lost to IEEE-754 and no
 * `BigInt()` throwing on a fraction that appeared out of nowhere.
 *
 * ## Why the currency's exponent decides everything
 *
 * KHR has no minor unit: 1,800 riel is `1800`, not `180000`. USD has two. Reading
 * "1800" against the wrong exponent misprices a station by 100×, so the exponent
 * comes from the same table `tariff.ts` renders with, and a currency missing from
 * it is refused rather than assumed.
 */

/** The server's `MAX_TARIFF_NAME`. */
export const PRICE_NAME_MAX = 120;

/** The server's `MAX_PRESETS`; pay-ui renders the buttons in a single row. */
export const PRICE_PRESETS_MAX = 8;

/**
 * The server's `MAX_MINOR`, in minor units. Absurd as a price and far short of
 * `int8`, which is the point: between the two lies a 500 on the driver's screen.
 */
const MAX_MINOR = 1_000_000_000_000n;

/** The currencies the server's CHECK allows, in the order the picker offers them. */
export const PRICE_CURRENCIES = ["USD", "KHR"] as const;

export interface PriceForm {
  readonly name: string;
  readonly currency: string;
  /** Major units — dollars, riel — per kWh. What the operator thinks in. */
  readonly price: string;
  readonly minAmount: string;
  readonly maxAmount: string;
  /** Comma-separated major amounts, ascending. Empty means no one-tap buttons. */
  readonly presets: string;
}

/** Exactly `POST /tariffs`'s body. Named so a drift shows up as a type error. */
export interface PriceBody {
  readonly name: string;
  readonly currency: string;
  readonly priceMinorPerKwh: string;
  readonly minAmountMinor: string;
  readonly maxAmountMinor: string;
  readonly presetAmountsMinor: readonly string[];
}

/** A message per field that has something wrong with it, keyed by the field. */
export type PriceIssues = Partial<Record<keyof PriceForm, string>>;

export type PriceDraft =
  | { readonly ok: true; readonly body: PriceBody }
  | { readonly ok: false; readonly issues: PriceIssues };

/**
 * The whole form at once, because half its rules are about two fields.
 *
 * A field that is merely still empty produces no message — the operator has not
 * typed yet, and a form that shouts before it has been filled in is one people
 * learn to submit past. It does produce `ok: false`, which is what keeps the
 * submit button disabled without inventing a second notion of "complete".
 */
export function readPriceForm(form: PriceForm): PriceDraft {
  const issues: PriceIssues = {};
  const exponent = MINOR_EXPONENT[form.currency];

  const name = form.name.trim();
  if (form.name !== "" && name === "") issues.name = "A price needs a name.";
  else if (name.length > PRICE_NAME_MAX) issues.name = `At most ${PRICE_NAME_MAX} characters.`;

  if (exponent === undefined) {
    // Unreachable from the picker. Kept because adding a currency is a server
    // change (§6.1's CHECK) and guessing an exponent for one is a 100× error.
    issues.currency = `${form.currency} is not a currency this system prices in.`;

    return { ok: false, issues };
  }

  const price = readAmount(form.price, exponent, issues, "price");
  const minAmount = readAmount(form.minAmount, exponent, issues, "minAmount");
  const maxAmount = readAmount(form.maxAmount, exponent, issues, "maxAmount");
  const presets = readPresets(form.presets, exponent, issues);

  /**
   * Reported on the maximum rather than on the form, because that is the field the
   * operator's cursor should land in — and the minimum is just as likely to be the
   * one they meant. Equal is allowed: it is a station that sells one fixed amount.
   */
  if (minAmount !== undefined && maxAmount !== undefined && maxAmount < minAmount) {
    issues.maxAmount = "Cannot be below the smallest order.";
  }

  if (presets !== undefined && minAmount !== undefined && maxAmount !== undefined) {
    const outOfRange = presets.some((amount) => amount < minAmount || amount > maxAmount);
    // A button nobody can press: the driver's own order is refused by these same
    // bounds, so the price is refused here rather than the dead button shipped.
    if (outOfRange) issues.presets = "Every button must be between the smallest and largest order.";
  }

  if (
    Object.keys(issues).length > 0 ||
    name === "" ||
    price === undefined ||
    minAmount === undefined ||
    maxAmount === undefined ||
    presets === undefined
  ) {
    return { ok: false, issues };
  }

  return {
    ok: true,
    body: {
      name,
      currency: form.currency,
      priceMinorPerKwh: price.toString(),
      minAmountMinor: minAmount.toString(),
      maxAmountMinor: maxAmount.toString(),
      presetAmountsMinor: presets.map((amount) => amount.toString()),
    },
  };
}

/**
 * One figure as minor units, or `undefined` when it is not a figure this currency
 * can charge.
 *
 * For echoing what the operator typed back at them while they are still typing —
 * `readPriceForm` cannot answer that, because a form with one field filled in is
 * not a valid form and it would report every other field as missing instead.
 */
export function minorOf(raw: string, currency: string): string | undefined {
  const exponent = MINOR_EXPONENT[currency];
  if (exponent === undefined) return undefined;

  const amount = toMinor(raw, exponent);

  return amount === undefined || amount <= 0n || amount > MAX_MINOR ? undefined : amount.toString();
}

/** Which field is being read, only so the "too many decimals" message can be exact. */
type AmountField = "price" | "minAmount" | "maxAmount";

function readAmount(
  raw: string,
  exponent: number,
  issues: PriceIssues,
  field: AmountField,
): bigint | undefined {
  if (raw.trim() === "") return undefined;

  const amount = toMinor(raw, exponent);
  if (amount === undefined) {
    issues[field] = precisionMessage(raw, exponent);
    return undefined;
  }

  if (amount <= 0n) {
    issues[field] = "Must be more than zero.";
    return undefined;
  }

  if (amount > MAX_MINOR) {
    issues[field] = "That is implausibly large — check the decimal point.";
    return undefined;
  }

  return amount;
}

/**
 * Says which of the two ways a figure can be unreadable it actually was.
 *
 * "Not a number" and "more precision than this currency has" are different
 * mistakes with different corrections, and a single message covering both would
 * leave an operator who typed `0.455` looking for a typo that is not there.
 */
function precisionMessage(raw: string, exponent: number): string {
  if (!/^\d+(\.\d+)?$/.test(clean(raw))) return "Type a plain amount, like 12.50.";

  return exponent === 0
    ? "This currency has no fractions — type a whole amount."
    : `At most ${exponent} decimal places.`;
}

function readPresets(raw: string, exponent: number, issues: PriceIssues): readonly bigint[] {
  const parts = raw
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part !== "");
  if (parts.length === 0) return [];

  if (parts.length > PRICE_PRESETS_MAX) {
    issues.presets = `At most ${PRICE_PRESETS_MAX} buttons.`;
    return [];
  }

  const amounts: bigint[] = [];
  for (const part of parts) {
    const amount = toMinor(part, exponent);
    if (amount === undefined || amount <= 0n || amount > MAX_MINOR) {
      issues.presets = `${part} is not an amount this currency can charge.`;
      return [];
    }
    amounts.push(amount);
  }

  /**
   * Not sorted for the operator. A descending list is a typo, and pay-ui renders
   * the buttons in the order given — quietly reordering them hides the mistake in
   * the one place it would have been visible.
   */
  const ascends = amounts.every(
    (amount, index) => index === 0 || amount > (amounts[index - 1] ?? 0n),
  );
  if (!ascends) {
    issues.presets = "Buttons must ascend, with no repeats.";
    return [];
  }

  return amounts;
}

/**
 * A stored price, back in the fields it was typed in.
 *
 * The other direction, and the one a price *change* needs: the form is in major units
 * and the row is in minor ones, so seeding a change form means undoing what
 * `readPriceForm` did. The pair has to compose — `readPriceForm(priceFormOf(row))`
 * must be `row` — or an operator opening the form and saving it untouched would
 * rotate the price to a figure they never set.
 *
 * A figure this system cannot price passes through as its raw minor units rather than
 * being rendered against a guessed exponent. The form then refuses it, which is the
 * honest outcome: there is no major-unit reading of a currency with no exponent.
 */
export function priceFormOf(price: {
  readonly name: string;
  readonly currency: string;
  readonly priceMinorPerKwh: string;
  readonly minAmountMinor: string;
  readonly maxAmountMinor: string;
  readonly presetAmountsMinor: readonly string[];
}): PriceForm {
  const exponent = MINOR_EXPONENT[price.currency];

  return {
    name: price.name,
    currency: price.currency,
    price: toMajor(price.priceMinorPerKwh, exponent),
    minAmount: toMajor(price.minAmountMinor, exponent),
    maxAmount: toMajor(price.maxAmountMinor, exponent),
    // The separator `readPresets` splits on, with the space it tolerates: what comes
    // out of the field is what went into it.
    presets: price.presetAmountsMinor.map((amount) => toMajor(amount, exponent)).join(", "),
  };
}

/**
 * Minor units to major, as a string, with the currency's full precision.
 *
 * `"50.00"` rather than `"50"`: the field is about to be read back by
 * `readPriceForm`, and both are the same figure to it — but the trailing zeros are
 * what tell an operator the field is in dollars before they start typing over it.
 */
function toMajor(minor: string, exponent: number | undefined): string {
  if (exponent === undefined || !/^\d+$/.test(minor)) return minor;
  if (exponent === 0) return minor;

  const padded = minor.padStart(exponent + 1, "0");

  return `${padded.slice(0, -exponent)}.${padded.slice(-exponent)}`;
}

/**
 * Major units to minor, without ever holding a float.
 *
 * Thousands separators and spaces are stripped first, because `money()` prints
 * them — refusing an operator's paste of our own output would be a refusal of our
 * own formatting.
 */
function toMinor(raw: string, exponent: number): bigint | undefined {
  const cleaned = clean(raw);
  if (!/^\d+(\.\d+)?$/.test(cleaned)) return undefined;

  const [whole = "", fraction = ""] = cleaned.split(".");
  if (fraction.length > exponent) return undefined;

  return BigInt(whole + fraction.padEnd(exponent, "0"));
}

function clean(raw: string): string {
  return raw.trim().replace(/[\s,]/g, "");
}
